<template>
  <div class="card" @click="$emit('openContent')">
    <div
      class="card__img-container"
      :class="{ 'card__img-container--open': isOpen }"
    >
      <div
        class="card__img-wrapper"
        :class="{ 'card__img-wrapper--open': isOpen }"
        :style="playlistColor"
      >
        <div class="card__duration-pill" v-if="playlistDuration">
          {{ playlistDuration }}
        </div>
        <PlaylistIcon />
      </div>
    </div>
    <div
      class="card__content-wrapper"
      :class="{ 'card__content-wrapper--open': isOpen }"
    >
      <div class="card__content-wrapper-main">
        <div class="card__content-wrapper-top-line">
          <span class="card__title">{{ playlist.name }}</span>
        </div>
        <div class="card__content-wrapper-mid-line">
          <p class="card__description">
            {{ playlist.description }}
          </p>
        </div>
        <div class="card__content-wrapper-bot-line">
          <div class="card__assigned-by-wrapper">
            <img
              :src="require('@/assets/images/instructor_fallback.png')"
              :ssrc="avatar"
              alt="Avatar"
              class="card__assigned-by-avatar"
            />
            <span class="card__assigned-by-name">
              Assigned by {{ assignedByName }}<br />
              {{ timeAgo }}
            </span>
          </div>
          <div v-if="!isMobile" class="card__divider" />
          <div class="card__courses-counter-wrapper">
            <span class="card__courses-counter">
              {{ this.coursesQuantity }} Courses
            </span>
          </div>
          <div class="card__divider" />
          <div class="card__progress-wrapper">
            <PlaylistProgress :value="playlist.user_status.progress" />
          </div>
        </div>
      </div>
      <div class="card__button-collapse">
        <ChevronDownIcon v-if="isOpen" :size="chevronIconSize" />
        <ChevronRightIcon v-else :size="chevronIconSize" />
      </div>
    </div>
  </div>
</template>

<script>
import { ChevronDownIcon, ChevronRightIcon } from 'vue-feather-icons'
import PlaylistIcon from '@/assets/icons/playlists/playlist_icon.svg'
import { fetchAvatar } from '@/models/profiles'
import { formatDistanceToNow } from 'date-fns'
import PlaylistProgress from './PlaylistProgress.vue'

export default {
  name: 'PlaylistCard',
  inject: ['getBreakpoint'],
  components: {
    PlaylistIcon,
    PlaylistProgress,
    ChevronDownIcon,
    ChevronRightIcon,
  },
  props: {
    playlist: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    chevronIconSize() {
      const breakpoint = this.getBreakpoint()

      switch (breakpoint) {
        case 'mobile':
          return '16'

        case 'tablet':
          return '20'

        default:
          return '24'
      }
    },
    isMobile() {
      return this.getBreakpoint() === 'mobile'
    },
    avatar() {
      return fetchAvatar(this.playlist.assigned_by.id)
    },
    timeAgo() {
      return formatDistanceToNow(new Date(this.playlist.created), {
        addSuffix: true,
      })
    },
    assignedByName() {
      const { first_name, last_name } = this.playlist.assigned_by

      return `${first_name} ${last_name}`
    },
    playlistColor() {
      const color = this.playlist.color

      return {
        '--playlist-bg-color': `${color}80`,
        '--playlist-ic-color': color,
      }
    },
    coursesQuantity() {
      return this.playlist.content.reduce(
        (quantity, group) =>
          quantity +
          group.content.filter(groupItem => groupItem.content_type === 'course')
            .length,
        0
      )
    },
    playlistDuration() {
      const { duration_in_seconds: duration } = this.playlist
      return this.$duration.durationFromSecs(duration)
    },
  },
}
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-direction: column;
  border-radius: 10px;

  @include tablet {
    flex-direction: row;
  }
  &:hover {
    cursor: pointer;

    .card__button-collapse {
      background: rgba(255, 255, 255, 0.1);
    }
    .card__content-wrapper {
      background: linear-gradient(
        112.2deg,
        rgba(187, 188, 199, 0.4) -46.39%,
        rgba(187, 188, 199, 0) 112.38%
      );
    }
  }
  &:active {
    .card__button-collapse {
      background: rgba(255, 255, 255, 0.2);
      color: var(--charcoal);
    }
    .card__content-wrapper {
      background: linear-gradient(
        111.02deg,
        rgba(18, 18, 18, 0.7) 3.86%,
        rgba(18, 18, 18, 0) 107.96%
      );
    }
  }

  &__img-container {
    background-color: var(--white);
    align-self: stretch;
    border-radius: 10px 10px 0px 0px;

    @include tablet {
      width: 190px;
      border-radius: 10px 0px 0px 10px;
    }
    &--open {
      border-bottom-left-radius: 0px;
    }
  }
  &__img-wrapper {
    position: relative;
    border-radius: inherit;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: inherit;

    background-color: var(--playlist-bg-color);

    svg {
      width: 117px;
      height: auto;
      rect {
        fill: transparent;
      }
      path {
        fill: var(--playlist-ic-color);
      }
    }

    &--open {
      border-bottom-left-radius: 0px;
    }
  }
  &__duration-pill {
    position: absolute;
    top: 10px;
    left: 10px;
    height: 24px;
    padding: 5px 8px;
    border-radius: 30px;
    color: var(--white);
    background: var(--dark-brighter);
    line-height: 14px;
    font-size: 12px;
  }

  &__content-wrapper {
    display: flex;
    flex: 1;
    padding: 10px;
    border-radius: 0px 0px 10px 10px;
    background: linear-gradient(
      104.99deg,
      rgba(62, 63, 73, 0.7) -13.91%,
      rgba(62, 63, 73, 0) 100.48%
    );
    @include tablet {
      border-radius: 0px 10px 10px 0px;
      padding: 20px;
    }

    &--open {
      border-radius: 0px;

      @include tablet {
        border-top-right-radius: 10px;
      }
    }
  }
  &__content-wrapper-main {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1;
  }

  &__content-wrapper-bot-line {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-top: 10px;
    gap: 10px 12px;

    font-size: rem(12px);
    line-height: 14px;
    color: var(--athens-gray);

    @include tablet {
      column-gap: 30px;
    }
    @include desktop {
      margin-top: 20px;
    }
  }
  &__title {
    font-family: var(--font-medium);
    color: var(--white);
    font-size: rem(16px);
    line-height: 23px;

    @include tablet {
      font-size: rem(18px);
      line-height: 24px;
    }
  }
  &__description {
    color: var(--light-blue-gray);
    overflow: hidden;
    text-overflow: ellipsis;
    max-lines: 2;
    height: 28px;
    font-size: rem(12px);
    line-height: 24px;
    margin-top: 5px;

    @include tablet {
      height: 40px;
      font-size: rem(14px);
      line-height: 20px;
    }
    @include desktop {
      margin: 0px;
      height: 24px;
      font-size: rem(16px);
      line-height: 24px;
    }
  }
  &__assigned-by-wrapper {
    display: flex;
    align-items: center;
    width: 100%;

    @include tablet {
      width: auto;
    }
  }
  &__assigned-by-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    margin-right: 10px;
  }
  &__courses-counter-wrapper {
    margin-left: 50px;
    @include tablet {
      margin: 0px;
    }
  }
  &__divider {
    height: 30px;
    border-left: 1px solid var(--tundora-gray);
    @include tablet {
      height: 40px;
    }
  }
  &__button-collapse {
    padding: 12px;
    border-radius: 8px;
    color: var(--white);

    width: 40px;
    height: 40px;

    @include tablet {
      margin-left: 10px;
      width: 44px;
      height: 44px;
    }
    @include desktop {
      display: flex;
      align-self: center;
      width: 48px;
      height: 48px;
    }
  }
}
</style>
